<template>
  <div class="plan-ahead-page" :class="{ 'no-plans': !hasPlans }">
    <div class="top-section">
      <div class="title">התוכניות הגדולות שלי</div>
      <div class="sub-title" v-if="!hasPlans">
        <div>חופשה מפנקת? רכב חדש?</div>
        <div>כדי באמת להיות על זה, אפשר להסתכל קדימה.</div>
      </div>
      <plans-total-data-section />
    </div>
    <div class="data-place-holder"></div>
    <div class="separator-container">
      <separator />
    </div>
    <div class="text-container">{{ text }}</div>
    <div class="plans-container">
      <plan class="plan" v-for="plan in enrichedPlans" :plan="plan" :key="plan._id" @edit-plan="openEditPage" />
    </div>
    <div class="add-plan-button-container" @click="() => openPlanFlow()">
      <icon icon-name="add_circle" :color="addPlanColor" fill></icon>
      <span class="text">הוספת תוכנית</span>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import Segment from '@/Segment';
import AddEditPlanFlow from '@/pages/responsive-pages/authenticated/navigation-pages/plan-ahead/plans-flow/AddEditPlanFlow';
import BaseUi from 'base-ui';
import PlansTotalDataSection
  from '@/pages/responsive-pages/authenticated/navigation-pages/plan-ahead/PlansTotalDataSection';
import casfhlowHistory from '@/store/utilities/cashflow-history';
import Plan from './Plan';
import PlanEditPage from './PlanEditPage';

export default {
  name: 'PlanAheadPage',
  components: {
    Plan,
    Separator: BaseUi.Separator,
    PlansTotalDataSection,
    Icon: BaseUi.Icon,
  },
  async created() {
    await this.initPlans();
    await this.initPlansSuggestions();
    if (!this.riseupGoal) {
      await this.initBudgets({ budgetDate: casfhlowHistory.getCurrentBudgetDate() });
    }
    Segment.trackUserGot('PlanAheadDashboard_Shown', {
      totalMonthlyAmount: this.totalPlansMonthlyAmount,
      numberOfPlans: this.plans.length,
      numberOfNotOnTrackPlans: this.enrichedPlans.filter(plan => !plan.isOnTrack).length,
    });
  },
  data() {
    return {
      addPlanColor: BaseUi.Colors.riseupGray2,
    };
  },
  computed: {
    ...mapState('planAhead', ['plans']),
    ...mapGetters('planAhead', ['hasPlans', 'totalPlansMonthlyAmount']),
    ...mapGetters('cashflowView', ['riseupGoal']),
    text() {
      return 'בסוף החודש, התזרים החיובי יוקצה לתוכניות הגדולות שלך, ויצבר בעו״ש. ככה יהיה אפשר לראות את ההתקדמות.';
    },
    enrichedPlans() {
      const reducedPlans = this.plans.reduce((acc, plan) => {
        const isOnTrack = acc.remaining - plan.monthlyTarget >= 0;
        const notOnTrackDiff = isOnTrack ? 0 : plan.monthlyTarget - acc.remaining;
        const amountDiff = isOnTrack ? plan.monthlyTarget : acc.remaining;
        return {
          plans: acc.plans.concat({ ...plan, isOnTrack, notOnTrackDiff }),
          remaining: acc.remaining - amountDiff,
        };
      }, { plans: [], remaining: this.riseupGoal });
      return reducedPlans.plans;
    },
  },
  methods: {
    ...mapActions('modalRootStore', ['openModal']),
    ...mapActions('planAhead', ['addPlan', 'initPlans', 'initPlansSuggestions']),
    ...mapActions('cashflowView', ['initBudgets']),
    openPlanFlow() {
      Segment.trackUserInteraction('PlanAheadDashboard_AddPlan_Clicked');
      this.openModal({
        component: AddEditPlanFlow,
        props: {
          onDone: this.addNewPlan,
        },
        popupAlignment: 'full-screen',
      });
    },
    openEditPage(plan) {
      this.openModal({
        component: PlanEditPage,
        props: {
          plan,
        },
        popupAlignment: 'full-screen',
      });
    },
    async addNewPlan() {
      await this.addPlan();
    },
  },
  watch: {
    async enrichedPlans() {
      Segment.trackUserGot('PlanAheadDashboard_PlansChanged', {
        totalMonthlyAmount: this.totalPlansMonthlyAmount,
        numberOfPlans: this.plans.length,
        numberOfNotOnTrackPlans: this.enrichedPlans.filter(plan => !plan.isOnTrack).length,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~base-ui/src/scss/riseup-colors';
@import './plan-ahead';

.plan-ahead-page {
  .top-section {
    height: $top-section-height;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 40px;
    position: relative;

    .title {
      font-weight: 700;
      font-size: 24px;
      line-height: 30px;
      padding-top: 7px;
    }

    .sub-title {
      display: flex;
      align-items: center;
      flex-direction: column;
      font-weight: 700;
      font-size: 16px;
      line-height: 120%;
    }
  }

  &.no-plans {
    .top-section {
      background: $riseup_gray_disabled;
    }

    .text-container {
      margin-bottom: 40px;
    }
  }

  .plans-container {
    .plan {
      margin-bottom: 15px;

      &:last-child {
        margin-bottom: 18px;
      }
    }
  }

  .data-place-holder {
    height: 90px;
  }

  .separator-container {
    margin: 0 10px;
  }

  .text-container {
    margin: 30px 30px 20px;
  }

  .add-plan-button-container {
    height: 150px;
    border: 2px dashed $riseup_gray_2;
    border-radius: 10px;
    margin: 0 25px 25px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;

    .text {
      font-weight: bold;
      padding-right: 8px;
      color: $riseup_gray_2;
    }
  }
}
</style>
